import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DirectorySelect } from '../resourceDirectory/DirectorySelect';
import { workflowApi } from './workflowApi';
import '../formFillComponents/DynamicFormComponents/FormInput.css';
import './ResourceWorkflow.css';

const unavailable = 'This review link is unavailable. Ask the sender for a new link.';
const readFragment = () => new URLSearchParams(window.location.hash.slice(1)).get('review') || '';

export default function ResourceReviewPage() {
  const [link, setLink] = useState(() => ({ token: readFragment(), revision: 0 }));
  const revision = useRef(0);
  const pending = useRef<symbol | null>(null);
  const [ready, setReady] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState('');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useLayoutEffect(() => {
    const meta = document.createElement('meta'); meta.name = 'referrer'; meta.content = 'no-referrer'; document.head.append(meta);
    // Initial fragment is captured only in memory above. Remove it before paint.
    window.history.replaceState(null, '', window.location.pathname);
    const consume = () => {
      const token = readFragment();
      window.history.replaceState(null, '', window.location.pathname);
      const next = ++revision.current;
      pending.current = null;
      setBusy(false); setReady(false); setSubmitted(false); setRating(''); setFeedback(''); setError('');
      setLink({ token, revision: next });
    };
    window.addEventListener('hashchange', consume);
    return () => { window.removeEventListener('hashchange', consume); meta.remove(); pending.current = null; };
  }, []);

  useEffect(() => {
    let live = true;
    setReady(false); setSubmitted(false); setError('');
    if (!link.token) { setError(unavailable); return () => { live = false; }; }
    void workflowApi.resolve(link.token).then(value => {
      if (live && revision.current === link.revision) { setReady(true); setSubmitted(value.submitted); }
    }).catch(() => { if (live && revision.current === link.revision) setError(unavailable); });
    return () => { live = false; };
  }, [link]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready || pending.current !== null || revision.current !== link.revision) return;
    const attempt = Symbol('resource-review-submit'); pending.current = attempt; setBusy(true); setError('');
    try {
      await workflowApi.submit(link.token, Number(rating), feedback);
      if (revision.current === link.revision && pending.current === attempt) { setSubmitted(true); setFeedback(''); }
    } catch {
      if (revision.current === link.revision && pending.current === attempt) setError('The review could not be submitted. The link may have expired; please try again or ask the sender.');
    } finally {
      if (pending.current === attempt) { pending.current = null; if (revision.current === link.revision) setBusy(false); }
    }
  };

  return <main className="rw-recipient"><section className="rw-panel"><h1>Resource feedback</h1>
    <p>This private link lets you review the resource connected to your referral. It does not provide access to the directory or participant records.</p>
    {error && <p role="alert">{error}</p>}
    {submitted ? <p role="status">Thank you. Your feedback has been received.</p> : ready ? <form onSubmit={submit}>
      <DirectorySelect label="Your rating" choices={[1, 2, 3, 4, 5].map(value => ({ value: String(value), label: `${value} out of 5` }))} multiple={false} values={[rating]} onChange={values => setRating(values[0] || '')} disabled={busy} />
      <label>Feedback (optional; please do not include personal or health information)<textarea className="form-v2-input" maxLength={2000} rows={5} value={feedback} onChange={event => setFeedback(event.target.value)} disabled={busy} /></label>
      <div className="rw-actions"><button disabled={busy || !rating}>{busy ? 'Submitting…' : 'Submit feedback'}</button></div>
    </form> : !error ? <p role="status">Checking review link…</p> : null}
  </section></main>;
}
